import React from "react";

export function DebugEnvInfo() {
  // Show in local dev (NODE_ENV=development), when ?debug=1, or when Amplify mock flags are set
  let debugParamEnabled = false;
  try {
    const params = new URLSearchParams(window.location.search);
    debugParamEnabled = params.get("debug") === "1";
  } catch {}
  const isLocalDevelopment =
    process.env.NODE_ENV === "development" ||
    debugParamEnabled;
  if (!isLocalDevelopment) {
    return null;
  }

  // Derive key endpoints for local development
  const hostname = (typeof window !== "undefined" && window.location?.hostname) || "localhost";
  const backendApiBase = process.env.REACT_APP_API_BASE_URL || `http://${hostname}:3001`;
  const reactDevServer = `${window.location.protocol}//${window.location.host}` || `http://${hostname}:3000`;
  const libsqlUrl = process.env.REACT_APP_LIBSQL_URL || "http://localhost:8080"; // Dev Container exposes on host:8080
  const graphqlLoggerUrl = process.env.REACT_APP_GRAPHQL_LOGGER_URL || ""; // Optional

  const statusColor = "#28a745"; // Green for local dev
  const statusText = "DEV";
  const statusIcon = "🟢";

  // Helper function to make endpoints clickable
  const renderEndpoint = (url: string, isClickable = false) => {
    const displayUrl = url?.replace(/^https?:\/\//, '') || 'N/A';
    
    if (isClickable && url) {
      return (
        <a 
          href={url.startsWith('http') ? url : `http://${displayUrl}`}
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            color: "#007bff", 
            textDecoration: "none",
            borderBottom: "1px dotted #007bff"
          }}
          onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
          onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
        >
          {displayUrl}
        </a>
      );
    }
    
    return <span>{displayUrl}</span>;
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f8fffe 0%, #e8f7f0 100%)",
        borderTop: `3px solid ${statusColor}`,
        padding: "12px 16px",
        fontSize: "11px",
        fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
        position: "relative"
      }}
    >
      {/* Status Badge - positioned absolutely in top right */}
      <div style={{
        position: "absolute",
        top: "8px",
        right: "12px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        backgroundColor: statusColor,
        color: "white",
        padding: "2px 6px",
        fontSize: "9px",
        fontWeight: "bold"
      }}>
        <span>{statusIcon}</span>
        <span>{statusText}</span>
      </div>

      {/* Service Information Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "12px"
      }}>
        {/* Backend API */}
        <div style={{ 
          backgroundColor: "rgba(255,255,255,0.8)",
          padding: "8px 10px",
          border: "1px solid rgba(0,0,0,0.1)"
        }}>
          <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#495057" }}>
            📡 Backend API
          </div>
          <div style={{ color: "#6c757d" }}>
            Base URL: {renderEndpoint(backendApiBase, true)}
          </div>
          <div style={{ color: "#6c757d" }}>
            Health: {renderEndpoint(`${backendApiBase.replace(/\/$/, "")}/health`, true)}
          </div>
        </div>

        {/* React Dev Server */}
        <div style={{ 
          backgroundColor: "rgba(255,255,255,0.8)",
          padding: "8px 10px",
          border: "1px solid rgba(0,0,0,0.1)"
        }}>
          <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#495057" }}>
            ⚛️ React Dev Server
          </div>
          <div style={{ color: "#6c757d" }}>
            URL: {renderEndpoint(reactDevServer, true)}
          </div>
        </div>

        {/* Database (libSQL/Turso) */}
        <div style={{ 
          backgroundColor: "rgba(255,255,255,0.8)",
          padding: "8px 10px",
          border: "1px solid rgba(0,0,0,0.1)"
        }}>
          <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#495057" }}>
            🗄️ Database (libSQL/Turso)
          </div>
          <div style={{ color: "#6c757d" }}>
            Endpoint: {renderEndpoint(libsqlUrl, true)}
          </div>
          <div style={{ color: "#6c757d" }}>
            Inside container: {renderEndpoint("http://libsql:8080", true)}
          </div>
        </div>

        {/* Optional: GraphQL Request Logger */}
        {graphqlLoggerUrl ? (
          <div style={{ 
            backgroundColor: "rgba(255,255,255,0.8)",
            padding: "8px 10px",
            border: "1px solid rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#495057" }}>
              🧪 GraphQL Logger (optional)
            </div>
            <div style={{ color: "#6c757d" }}>
              URL: {renderEndpoint(graphqlLoggerUrl, true)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
